/**
 * Moteur de la phase de revelation.
 *
 * Il fait deux choses que la scene precedente ne savait pas faire ensemble :
 *  - un zoom d'entree automatique sur la zone, puis pan/zoom LIBRES ;
 *  - un curseur vertical qui separe la retouche (a gauche) de l'œuvre
 *    d'origine (a droite), et qui continue de fonctionner apres qu'on a
 *    deplace ou zoome la vue.
 *
 * Les deux cohabitent parce qu'ils ne se disputent pas le meme geste : la vue
 * repond au glisser sur le canvas, le curseur a une poignee qui lui est propre
 * (un element DOM au-dessus, cf. RevealScreen). Le moteur ne connait donc que
 * la valeur du curseur, jamais l'evenement qui la change.
 *
 * Comme les autres moteurs, il vit hors de React : aucun setState par frame.
 */
import type { DrawZone, Drawing } from '@retusch/shared';
import { ArtworkRenderer } from './canvas/ArtworkRenderer.js';
import { Compositor } from './canvas/Compositor.js';
import { StrokeCapture } from './canvas/StrokeCapture.js';
import { ZoneCanvas } from './canvas/ZoneCanvas.js';
import { fitViewport, focusViewport, zoneToRect, type Rect, type Vec2 } from './canvas/geometry.js';
import { ZONE_CANVAS_WIDTH } from './canvas/renderConfig.js';
import { naturalSize } from './artwork.js';

const GOLD = '#e8b84b';

/** Part de la largeur de vue occupee par la zone a la fin du zoom d'entree. */
const REVEAL_ZOOM_RATIO = 0.55;

export interface RevealEngineOptions {
  readonly canvas: HTMLCanvasElement;
  readonly image: HTMLImageElement;
  readonly zone: DrawZone;
  readonly drawing: Drawing;
  readonly palette: readonly string[];
  /** Duree du zoom d'entree. 0 pour cadrer immediatement (reduced-motion). */
  readonly introMs: number;
  /** Duree de l'aller-retour automatique du curseur, apres le zoom. */
  readonly autoSlideMs: number;
}

export class RevealEngine {
  private readonly options: RevealEngineOptions;
  private readonly renderer: ArtworkRenderer;
  private readonly compositor: Compositor;
  private readonly zoneCanvas: ZoneCanvas;
  private readonly capture: StrokeCapture;
  private readonly zoneRect: Rect;

  private startedAt = 0;
  /** Position du curseur, 0 (tout l'original) a 1 (toute la retouche). */
  private split = 1;
  /** Le joueur a-t-il pris la main sur le curseur ? */
  private splitTouched = false;
  /** Le joueur a-t-il pris la main sur la vue ? */
  private viewTouched = false;
  private onSplitChange: ((value: number) => void) | null = null;

  constructor(options: RevealEngineOptions) {
    this.options = options;

    const imageSize = naturalSize(options.image);
    this.zoneRect = zoneToRect(options.zone, imageSize);

    this.renderer = new ArtworkRenderer(options.canvas, imageSize);
    this.compositor = new Compositor(options.image, imageSize, options.zone, ZONE_CANVAS_WIDTH);
    this.zoneCanvas = new ZoneCanvas(
      this.zoneRect.height / this.zoneRect.width,
      options.palette,
    );
    this.zoneCanvas.replay(options.drawing);
    this.compositor.invalidateZone();

    this.capture = new StrokeCapture(
      {
        element: options.canvas,
        mode: 'inspect',
        toView: (x, y) => this.renderer.clientToView(x, y),
        toZone: (x, y) => this.renderer.clientToNormalized(x, y),
        minPointDistance: () => 1,
        // Rien ne se dessine ici : seuls les gestes de vue nous interessent.
        canDraw: () => false,
      },
      {
        onGesture: (delta) => {
          // Le premier geste coupe le zoom d'entree : rien n'est plus penible
          // qu'une vue qui continue de bouger toute seule sous le doigt.
          this.viewTouched = true;
          this.renderer.cancelAnimation();
          if (delta.dx !== 0 || delta.dy !== 0) this.renderer.panBy(delta.dx, delta.dy);
          if (delta.scale !== 1) this.renderer.zoomAt(delta.scale, delta.center);
        },
      },
    );
  }

  /** Fraction 0-1 correspondant a une abscisse en px CSS. */
  splitFromView(x: number): number {
    const width = Math.max(this.renderer.view.width, 1);
    return Math.min(Math.max(x / width, 0), 1);
  }

  /** Abscisse du curseur en px CSS, pour positionner la poignee. */
  get splitPx(): number {
    return this.split * this.renderer.view.width;
  }

  get splitValue(): number {
    return this.split;
  }

  /** Deplacement du curseur par la poignee. Fige l'aller-retour automatique. */
  setSplit(value: number): void {
    this.splitTouched = true;
    this.split = Math.min(Math.max(value, 0), 1);
    this.renderer.markDirty();
  }

  /** Notifie la position a chaque frame, pour la poignee DOM. */
  onSplit(listener: (value: number) => void): void {
    this.onSplitChange = listener;
  }

  start(): void {
    this.startedAt = performance.now();
    this.capture.attach();
    this.renderer.start(this.render);

    // Cadrage d'entree : du tableau entier vers la zone.
    this.renderer.setViewport(fitViewport(this.renderer.image, this.renderer.view), false);
    void this.renderer.animateTo(this.focusTarget(), this.options.introMs);
  }

  resize(): void {
    this.renderer.resize();
    // Tant que le joueur n'a pas touche a la vue, on la recadre : sinon un
    // simple changement de taille de fenetre lui volerait son cadrage.
    if (!this.viewTouched) this.renderer.setViewport(this.focusTarget(), false);
  }

  destroy(): void {
    this.capture.detach();
    this.renderer.stop();
  }

  private focusTarget(): ReturnType<typeof focusViewport> {
    return focusViewport(this.zoneRect, this.renderer.view, REVEAL_ZOOM_RATIO);
  }

  private readonly render = (
    ctx: CanvasRenderingContext2D,
    viewport: { scale: number; tx: number; ty: number },
    now: number,
  ): void => {
    const view = this.renderer.view;

    // Aller-retour automatique du curseur, le temps de montrer ce qu'il fait.
    // Il s'arrete des que le joueur y touche.
    const elapsed = now - this.startedAt;
    if (!this.splitTouched && elapsed < this.options.autoSlideMs) {
      this.split = Math.sin((elapsed / this.options.autoSlideMs) * Math.PI * 2) * 0.5 + 0.5;
      // Le renderer ne rappelle que sur invalidation : tant que le curseur
      // bouge tout seul, c'est a nous de redemander la frame suivante.
      this.renderer.markDirty();
    }
    this.onSplitChange?.(this.split);

    // A droite du curseur : l'œuvre d'origine, sans aucun masque.
    this.compositor.draw(ctx, viewport, null, { grain: true, feather: true, zone: 'artwork' });

    // A gauche : la version retouchee.
    const splitX = view.width * this.split;
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, splitX, view.height);
    ctx.clip();
    this.compositor.draw(ctx, viewport, this.zoneCanvas.canvas, {
      grain: true,
      feather: true,
      zone: 'retouched',
    });
    ctx.restore();

    // Trait du curseur, puis lisere de la zone.
    ctx.save();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 2;
    ctx.shadowColor = GOLD;
    ctx.shadowBlur = 16;
    ctx.beginPath();
    ctx.moveTo(splitX, 0);
    ctx.lineTo(splitX, view.height);
    ctx.stroke();

    const topLeft: Vec2 = {
      x: this.zoneRect.x * viewport.scale + viewport.tx,
      y: this.zoneRect.y * viewport.scale + viewport.ty,
    };
    ctx.strokeRect(
      topLeft.x,
      topLeft.y,
      this.zoneRect.width * viewport.scale,
      this.zoneRect.height * viewport.scale,
    );
    ctx.restore();
  };
}
